/**
 * Copyright (c) 2017 CRF Box Oy. All Rights Reserved. Company Confidential.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loading from '../loading/loading.component';
import Notification from '../common/notification/notification.component';
import { Grid, Button, Pagination } from 'tc-client-common';
import SelectComponent from '../common/select/select';
import SearchComponent from '../common/search/search';
import { Link } from 'react-router-dom';
import { head } from 'ramda';
import { userManagementPeople, organization_details, userManagementStudy } from '../../constants/localUrls';
import UserIcon from '../../assets/images/icons/user-picture.svg';
import { applyOrganizationFilterAndSort, maybeMultiple } from './utils/people.utils';
import {
  MULTIPLE_STUDIES,
  MULTIPLE_ROLES,
  VIEW_ORGANIZATION,
  OWN_ORGANIZATION,
  ALL_ORGANIZATIONS,
  USER_MANAGEMENT,
  CLEAR
} from '../../const';
import { compose } from 'ramda';

const { usePagination, withPagination } = Pagination;
const PAGE_SIZE = 10;
const columnDefinition = [
  { label: 'Name', size: 25 },
  { label: 'Username', size: 25 },
  { label: 'Role', size: 25 },
  { label: 'Study', size: 25 }
];
const INITIAL_ORG_FILTER = { value: 'All', label: 'All' };
const INITIAL_PEOPLE_FILTER = { value: 'name', label: 'Name' };
const People = ({ sponsor, fetchOrganizations, organizations, isFetching, hiredOrgGid, error }) => {
  const [organizationFilter, setOrganizationFilter] = useState({ value: 'All', label: 'All' });
  const [peopleSortFn, setPeopleSortFn] = useState({ value: 'name', label: 'Name' });
  const [searchBy, setSearchBy] = useState('');
  const [loading, setLoading] = useState(false);
  const paginationApi = usePagination();
  useEffect(() => {
    const contentComponentWrapper = document.getElementById('content-component-wrapper');
    contentComponentWrapper && (contentComponentWrapper.scrollTop = 0);
    selectPage({ nextPage: 1, pageSize: PAGE_SIZE });
  }, []);

  const selectPage = ({ nextPage, pageSize, onSuccess = () => {} }) => {
    paginationApi.show();
    paginationApi.loading(true);
    setLoading(true);
    fetchOrganizations(sponsor.databasename, false, (nextPage - 1) * pageSize, pageSize)
      .then((data) => {
        onSuccess();
        setLoading(false);
        if (data && data.organizations) {
          if (data.organizations.data.length === 0) {
            paginationApi.hide();
          } else {
            paginationApi.show();
          }
        }
        const totalOrganizations = data?.organizations?.total || null;
        const pageCount = Math.ceil(totalOrganizations / pageSize);
        paginationApi.update({ pageCount, pageSize, itemsCount: totalOrganizations, currentPage: nextPage });
        paginationApi.loading(false);
      })
      .catch((error) => console.log(error));
  };

  paginationApi.selectPage(selectPage);

  const organizationFilterChange = (organizationFilter) => {
    setOrganizationFilter(organizationFilter);
  };

  const clearSortAndFilter = () => {
    setOrganizationFilter(INITIAL_ORG_FILTER);
    setPeopleSortFn(INITIAL_PEOPLE_FILTER);
    paginationApi.show();
  };

  const sortPeopleChange = (peopleSortFn) => setPeopleSortFn(peopleSortFn);
  const searchInOrganizations = (searchBy) => {
    setSearchBy(searchBy);
  };

  const getBannerLabel = ({ name, id, label }) => {
    return () => (
      <span>
        <span>{label || name}</span>
        <Link
          className="link view-organization"
          to={{
            pathname: organization_details(id, name),
            state: {
              forBreadcrumb: { organizationId: id, organizationName: name }
            }
          }}
        >
          {VIEW_ORGANIZATION}
        </Link>
      </span>
    );
  };

  const getRowsData = ({ id, name, people }) => {
    const nameColumn = (row) => () => (
      <span className="people-column">
        <img className="people-icon" src={UserIcon} />
        <Link
          className="link"
          to={{
            pathname: userManagementPeople(row.userId),
            state: {
              user: row,
              forBreadcrumb: { ...row, organizationId: id, organizationName: name }
            }
          }}
        >
          <span>{`${row.firstName} ${row.lastName}`}</span>
        </Link>
      </span>
    );

    const roleColumn = (roles) => head(roles).roleNameDescription;

    const studyColumn = (row) => () => {
      const study = head(row.studies);

      return (
        <Link
          className="link"
          to={{
            pathname: userManagementStudy(study.gid),
            state: {
              forBreadcrumb: {
                ...row,
                studyGid: study.gid,
                studyName: study.name
              }
            }
          }}
        >
          {study.name}
        </Link>
      );
    };

    const getSingleRow = (row) => {
      const assignmentId = row.assignmentId || '';
      const username = row.username || '';
      const role = maybeMultiple({
        multiple: () => MULTIPLE_ROLES,
        single: () => roleColumn(row.roles)
      })(row.roles);
      const study = maybeMultiple({
        multiple: () => MULTIPLE_STUDIES,
        single: () => studyColumn(row)
      })(row.studies);

      return {
        id: assignmentId,
        columns: [
          { columnData: nameColumn(row), id: `${assignmentId}-1`, customColor: '#0073CF' },
          { columnData: username, id: `${assignmentId}-2` },
          { columnData: role, id: `${assignmentId}-3` },
          { columnData: study, id: `${assignmentId}-4` }
        ]
      };
    };

    return people.map(getSingleRow);
  };

  if (error) {
    selectPage({ nextPage: 1, pageSize: PAGE_SIZE });
    return <Notification type="error" data={error} />;
  }
  if (!organizations || isFetching || loading) {
    paginationApi.hide();
    return <Loading />;
  }
  if (!organizations.length || organizations.length === 0) {
    return <Notification type="info" data="No organizations found." />;
  }

  return (
    <div id="people-page">
      <div className="page-title">
        <h3>{USER_MANAGEMENT}</h3>
      </div>
      <div className="people-page-filters">
        <SelectComponent
          id="organization-filter-selector"
          label="Organization"
          selectedValue={organizationFilter}
          searchable
          onChange={organizationFilterChange}
          options={[
            { value: 'All', label: 'All' },
            ...organizations.map(({ gid, id: value, name }) => ({
              value,
              label: gid === hiredOrgGid ? OWN_ORGANIZATION : name
            }))
          ]}
        />

        <SelectComponent
          id="people-sort-selector"
          label="Sort by"
          selectedValue={peopleSortFn}
          onChange={sortPeopleChange}
          options={[
            { value: 'name', label: 'Name' },
            { value: 'userName', label: 'Username' },
            { value: 'role', label: 'Role' },
            { value: 'study', label: 'Study' },
            { value: 'createdOn', label: 'Recently added' }
          ]}
        />
        <Button theme="default large-button people-page-filters-clear" handleClick={clearSortAndFilter} label={CLEAR} />

        <div className="people-page-filters-search">
          <SearchComponent placeholder="Enter search item here" onChange={searchInOrganizations} />
        </div>
      </div>

      <div id="people-component">
        {organizationFilter.value === 'All' && <Grid bannerLabel={ALL_ORGANIZATIONS} />}
        {applyOrganizationFilterAndSort({ organizationFilter, peopleSortFn, searchBy, hiredOrgGid })(organizations).map(
          ({ id, name, people, gid }) => (
            <Grid
              key={id}
              disabled={false}
              bannerLabel={getBannerLabel({ id, name, label: hiredOrgGid === gid ? OWN_ORGANIZATION : name })}
              columnDefinition={columnDefinition}
              rows={getRowsData({ id, name, people })}
            />
          )
        )}
      </div>
    </div>
  );
};

People.propTypes = {
  sponsor: PropTypes.object,
  fetchOrganizations: PropTypes.func.isRequired,
  organizations: PropTypes.array,
  error: PropTypes.string,
  isFetching: PropTypes.bool,
  hiredOrgGid: PropTypes.string
};

export default compose(withPagination)(People);
